/**
 * Funnel instrumentation (§9.7, FN-1–FN-4). Events carry ONLY enumerated
 * values — stage, route, verdict, category, threshold basis — never a
 * salary, a savings figure or a date (§3.6.9: no personal data in
 * telemetry). Events queue on window.__verity until a sink (Application
 * Insights, workstream D) is wired; the vocabulary is fixed here so day-one
 * numbers exist the moment the sink lands.
 *
 * THE SINK IS NOW WIRED: batched to POST /api/events, flushed when the page
 * is hidden. Our own endpoint rather than a third-party script, for two
 * reasons that both bind — `connect-src 'self'` forbids posting anywhere
 * else, and prohibition 10 keeps personal data in the UK or EU, which an
 * analytics CDN would break before it collected anything.
 *
 * No cookie, no visitor id, no IP stored, nothing joining one event to the
 * next. That is what makes this lawful with no consent banner, and it is why
 * only aggregate rates are answerable — never a per-person funnel.
 */

export type EventName =
  // Without a start event there is no denominator: "150 completions" says
  // nothing without knowing whether 200 people began or 3,000 did.
  | "calculator_started"
  | "calculator_completed"
  | "calculator_out_of_scope"
  | "share_clicked"
  | "evidence_viewed"
  // F1: pressed "start your application" at the end of the check. The one
  // conversion event in the funnel, and the denominator for everything after.
  | "application_started"
  // F5: came back and saved a changed answer. Distinct from a first check,
  // because a revision is a returning customer and counting it as a new one
  // would overstate the top of the funnel.
  | "case_revised";

type Props = Partial<{
  stage: string;
  route: string;
  verdict: string;
  category: string;
  threshold_basis: string;
  reason: string; // out-of-scope flag name only
}>;

interface QueuedEvent {
  name: EventName;
  props: Props;
  source: { referrer: string; utm: Record<string, string> };
}

declare global {
  interface Window {
    __verity?: { events: QueuedEvent[] };
  }
}

function source(): QueuedEvent["source"] {
  const utm: Record<string, string> = {};
  for (const [key, value] of new URLSearchParams(window.location.search)) {
    if (key.startsWith("utm_")) utm[key] = value;
  }
  return { referrer: document.referrer, utm };
}

export function track(name: EventName, props: Props = {}): void {
  const queue = (window.__verity ??= { events: [] });
  queue.events.push({ name, props, source: source() });
  // The wizard ends on the result page and people close the tab from there,
  // so waiting for a full batch would lose exactly the events that matter.
  if (queue.events.length >= BATCH) flush();
}

const BATCH = 5;

/**
 * Send whatever is queued, and clear it.
 *
 * `sendBeacon` because a `fetch` started during page-hide is routinely
 * cancelled — the completion event is the last thing a visitor triggers
 * before leaving, and losing it would bias every funnel number towards
 * abandonment. Analytics never blocks or throws: a metric that breaks the
 * page is worse than no metric.
 */
export function flush(): void {
  const queue = window.__verity;
  if (!queue || queue.events.length === 0) return;
  const batch = queue.events.splice(0, queue.events.length);

  const body = JSON.stringify({
    events: batch.map((e) => ({
      name: e.name,
      props: e.props,
      // Hostname only — a full referrer can carry a search query or the title
      // of a forum thread about someone's own immigration case.
      referrer_host: hostOnly(e.source.referrer),
      utm: e.source.utm,
    })),
  });

  try {
    if (typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon("/api/events", new Blob([body], { type: "application/json" }));
      return;
    }
    void fetch("/api/events", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Deliberately silent.
  }
}

function hostOnly(referrer: string): string | null {
  if (!referrer) return null;
  try {
    const host = new URL(referrer).hostname;
    return host === window.location.hostname ? null : host;
  } catch {
    return null;
  }
}

if (typeof document !== "undefined") {
  // `visibilitychange` rather than `unload`: the latter is unreliable on
  // mobile Safari, which is where a good share of this traffic will be.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", flush);
}
